import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { Check, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";

type EmailSignupFormProps = {
  source: string;
  compact?: boolean;
  onSuccess?: () => void;
};

export default function EmailSignupForm({
  source,
  compact = false,
  onSuccess,
}: EmailSignupFormProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setError(null);
    try {
      await api.post("/api/subscribe", {
        email,
        firstName: firstName || undefined,
        source,
      });
      setStatus("success");
      setEmail("");
      setFirstName("");
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Email signup failed");
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <motion.div
        className="flex flex-col items-center text-center"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <motion.div
          className="mb-4 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: `${colors.forestGreen}18`, color: colors.forestGreen }}
          initial={{ scale: 0.65 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 360, damping: 18 }}
        >
          <Check size={30} />
        </motion.div>
        <p style={{ fontFamily: fonts.body, fontWeight: 800, color: colors.navy }}>
          You&apos;re in.
        </p>
        <p className="mt-1 text-sm" style={{ fontFamily: fonts.body, color: `${colors.navy}99` }}>
          Watch your inbox for the next note.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "grid gap-3" : "mx-auto grid max-w-md gap-3"}>
      {!compact && (
        <TextInput
          label="First name"
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
        />
      )}
      <TextInput
        label="Email address"
        name="email"
        type="email"
        required
        autoComplete="email"
        placeholder="your@email.com"
        value={email}
        error={status === "error" ? error : null}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Button
        type="submit"
        disabled={status === "loading"}
        icon={<Mail size={16} />}
        className="w-full"
      >
        {status === "loading" ? "Joining..." : "Count Me In"}
      </Button>
    </form>
  );
}
